import { useState, useEffect } from 'react';
import {
    Container,
    Title,
    Button,
    Table,
    Modal,
    TextInput,
    Textarea,
    Stack,
    Badge,
    ActionIcon,
    Group,
    Skeleton,
    Text,
    Tooltip,
    Select,
    Pagination,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash, IconTestPipe, IconPencil, IconPower } from '@tabler/icons-react';
import { sitesApi } from '../api/client';

interface Site {
    id: number;
    site_name: string;
    url: string;
    username: string;
    description?: string;
    is_active: boolean;
    created_at: string;
    user_id?: number | null;
    owner_username?: string | null;
}

export default function Sites({ isAdmin = false }: { isAdmin?: boolean }) {
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 25;
    const [addModalOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
    const [editModalOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
    const [deleteModalOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [editTarget, setEditTarget] = useState<Site | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);

    const addForm = useForm({
        initialValues: {
            site_name: '',
            url: '',
            username: '',
            auth_token: '',
            description: '',
        },
        validate: {
            site_name: (value) => (value ? null : 'Site name is required'),
            url: (value) => (value ? null : 'URL is required'),
            username: (value) => (value ? null : 'Username is required'),
            auth_token: (value) => (value ? null : 'Application Password is required'),
        },
    });

    const editForm = useForm({
        initialValues: {
            site_name: '',
            url: '',
            username: '',
            auth_token: '',
            description: '',
        },
        validate: {
            site_name: (value) => (value ? null : 'Site name is required'),
            url: (value) => (value ? null : 'URL is required'),
            username: (value) => (value ? null : 'Username is required'),
        },
    });

    const loadSites = async () => {
        setLoading(true);
        try {
            const result = await sitesApi.getAll({ search: debouncedSearch, status: statusFilter, page, pageSize });
            setSites(Array.isArray(result.data) ? (result.data as unknown as Site[]) : []);
            setTotal(result.total);
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to load sites',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSites();
    }, [debouncedSearch, statusFilter, page]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, statusFilter]);

    const handleSubmit = async (values: typeof addForm.values) => {
        try {
            await sitesApi.create(values);
            notifications.show({
                title: 'Success',
                message: 'Site added successfully',
                color: 'green',
            });
            addForm.reset();
            closeAdd();
            loadSites();
        } catch (error: any) {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to add site',
                color: 'red',
            });
        }
    };

    const openEditModal = (site: Site) => {
        setEditTarget(site);
        editForm.setValues({
            site_name: site.site_name,
            url: site.url,
            username: site.username,
            auth_token: '',
            description: site.description ?? '',
        });
        openEdit();
    };

    const handleEdit = async (values: typeof editForm.values) => {
        if (!editTarget) return;
        const updates: Record<string, string> = {
            site_name: values.site_name,
            url: values.url,
            username: values.username,
            description: values.description,
        };
        if (values.auth_token) updates.auth_token = values.auth_token;
        try {
            await sitesApi.update(editTarget.id, updates);
            notifications.show({
                title: 'Success',
                message: 'Site updated successfully',
                color: 'green',
            });
            closeEdit();
            loadSites();
        } catch (error: any) {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to update site',
                color: 'red',
            });
        }
    };

    const confirmDelete = (site: Site) => {
        setDeleteTarget(site);
        openDelete();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await sitesApi.delete(deleteTarget.id);
            notifications.show({
                title: 'Success',
                message: 'Site deleted successfully',
                color: 'green',
            });
            closeDelete();
            loadSites();
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to delete site',
                color: 'red',
            });
        }
    };

    const handleToggle = async (site: Site) => {
        try {
            await sitesApi.update(site.id, { is_active: !site.is_active });
            loadSites();
        } catch (error) {
            notifications.show({ title: 'Error', message: 'Failed to toggle site', color: 'red' });
        }
    };

    const handleTest = async (id: number, siteName: string) => {
        try {
            const result = await sitesApi.test(id);
            if (result.connected) {
                notifications.show({
                    title: 'Success',
                    message: `Connection to ${siteName} successful`,
                    color: 'green',
                });
            } else {
                notifications.show({
                    title: 'Connection Failed',
                    message: `Cannot connect to ${siteName}`,
                    color: 'red',
                });
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to test connection',
                color: 'red',
            });
        }
    };

    return (
        <Container size="xl">
            <Group justify="space-between" mb="xl">
                <Title order={1}>WordPress Sites</Title>
                <Button leftSection={<IconPlus size={16} />} onClick={openAdd}>
                    Add Site
                </Button>
            </Group>

            <Group mb="md">
                <TextInput
                    placeholder="Search by name, URL, username or owner..."
                    leftSection={<IconSearch size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    style={{ flex: 1 }}
                />
                <Select
                    data={[
                        { value: 'all', label: 'All statuses' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter((value as 'all' | 'active' | 'inactive') ?? 'all')}
                    allowDeselect={false}
                    w={160}
                />
            </Group>

            <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">
                    {sites.length} of {total} result{total !== 1 ? 's' : ''}
                </Text>
                <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / pageSize))} size="sm" />
            </Group>

            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>URL</Table.Th>
                        <Table.Th>Username</Table.Th>
                        {isAdmin && <Table.Th>Owner</Table.Th>}
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {loading ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <Table.Tr key={i}>
                                <Table.Td><Skeleton height={20} /></Table.Td>
                                <Table.Td><Skeleton height={20} /></Table.Td>
                                <Table.Td><Skeleton height={20} /></Table.Td>
                                <Table.Td><Skeleton height={20} width={60} /></Table.Td>
                                <Table.Td><Skeleton height={20} width={90} /></Table.Td>
                            </Table.Tr>
                        ))
                    ) : sites.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={isAdmin ? 6 : 5}>
                                <Text c="dimmed" ta="center" py="md">
                                    {search || statusFilter !== 'all'
                                        ? 'No sites match your search or filters.'
                                        : 'No sites yet. Add one to get started.'}
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    ) : (
                        sites.map((site) => (
                            <Table.Tr key={site.id}>
                                <Table.Td>{site.site_name}</Table.Td>
                                <Table.Td>{site.url}</Table.Td>
                                <Table.Td>{site.username}</Table.Td>
                                {isAdmin && (
                                    <Table.Td>
                                        <Text size="sm">{site.owner_username ?? '—'}</Text>
                                        {site.user_id != null && <Text size="xs" c="dimmed"># {site.user_id}</Text>}
                                    </Table.Td>
                                )}
                                <Table.Td>
                                    <Badge color={site.is_active ? 'green' : 'gray'}>
                                        {site.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Group gap="xs">
                                        <Tooltip label="Test connection">
                                            <ActionIcon
                                                color="blue"
                                                variant="light"
                                                onClick={() => handleTest(site.id, site.site_name)}
                                                aria-label="Test connection"
                                            >
                                                <IconTestPipe size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label="Edit site">
                                            <ActionIcon
                                                color="gray"
                                                variant="light"
                                                onClick={() => openEditModal(site)}
                                                aria-label="Edit site"
                                            >
                                                <IconPencil size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label={site.is_active ? 'Disable site' : 'Enable site'}>
                                            <ActionIcon
                                                color={site.is_active ? 'orange' : 'green'}
                                                variant="light"
                                                onClick={() => handleToggle(site)}
                                                aria-label={site.is_active ? 'Disable site' : 'Enable site'}
                                            >
                                                <IconPower size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        {isAdmin && (
                                            <Tooltip label="Delete site">
                                                <ActionIcon
                                                    color="red"
                                                    variant="light"
                                                    onClick={() => confirmDelete(site)}
                                                    aria-label="Delete site"
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))
                    )}
                </Table.Tbody>
            </Table>

            <Group justify="space-between" mt="md">
                <Text size="sm" c="dimmed">
                    {sites.length} of {total} result{total !== 1 ? 's' : ''}
                </Text>
                <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / pageSize))} />
            </Group>

            {/* Add Site Modal */}
            <Modal opened={addModalOpened} onClose={closeAdd} title="Add WordPress Site" size="lg">
                <form onSubmit={addForm.onSubmit(handleSubmit)}>
                    <Stack>
                        <TextInput label="Site Name" placeholder="My WordPress Site" required {...addForm.getInputProps('site_name')} />
                        <TextInput label="URL" placeholder="http://localhost:8081" required {...addForm.getInputProps('url')} />
                        <TextInput label="Username" placeholder="admin" required {...addForm.getInputProps('username')} />
                        <TextInput label="Application Password" placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" required {...addForm.getInputProps('auth_token')} />
                        <Textarea label="Description" placeholder="Optional description" {...addForm.getInputProps('description')} />
                        <Button type="submit">Add Site</Button>
                    </Stack>
                </form>
            </Modal>

            {/* Edit Site Modal */}
            <Modal opened={editModalOpened} onClose={closeEdit} title="Edit WordPress Site" size="lg">
                <form onSubmit={editForm.onSubmit(handleEdit)}>
                    <Stack>
                        <TextInput label="Site Name" required {...editForm.getInputProps('site_name')} />
                        <TextInput label="URL" required {...editForm.getInputProps('url')} />
                        <TextInput label="Username" required {...editForm.getInputProps('username')} />
                        <TextInput label="New Application Password" placeholder="Leave empty to keep current" {...editForm.getInputProps('auth_token')} />
                        <Textarea label="Description" {...editForm.getInputProps('description')} />
                        <Button type="submit">Save Changes</Button>
                    </Stack>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal opened={deleteModalOpened} onClose={closeDelete} title="Delete site" size="sm">
                <Stack>
                    <Text>
                        Delete <strong>{deleteTarget?.site_name}</strong>? This action cannot be undone.
                    </Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeDelete}>Cancel</Button>
                        <Button color="red" onClick={handleDelete}>Delete</Button>
                    </Group>
                </Stack>
            </Modal>
        </Container>
    );
}
